import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Button,
  Grid,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ControlSummary from "./ControlSummary";
import OpenTasksCard from "./OpenTasksCard";
import AIEvidenceStrip from "./AIEvidenceStrip";
import { GET_AI_EVIDENCE_SUMMARY } from "../../../apis/integrated-evidence/aiEvidenceApis";
import ReadinessChip from "../../../components/ReadinessChip/ReadinessChip";
import { useQuery } from "@apollo/client";
import {
  GET_MODULE_LIST,
  GET_FRAMEWORK_DETAIL_BY_ID,
  GET_USER_DETAILS,
} from "../../../apis/dashboard-apis/dashboardDetailApi";
import useIsDemoInstance from "../../Chat/Chat-Hooks/useIsDemoInstance";
import useAppStore from "../../../stores";
import { CheckStatus } from "../../../utils/enums";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../../../components/AccessControl";

const Dashboard = () => {
  const theme = useTheme();
  const { data: userData } = useQuery(GET_USER_DETAILS);

  // Get selected framework from store
  const selectedFrameworkId = useAppStore((state) => state.selectedFrameworkId);
  const selectedFramework = useAppStore((state) => state.selectedFramework);
  const frameworkTerminology = useAppStore(
    (state) => state.frameworkTerminology
  );

  const {
    data,
    loading,
    refetch: refetchModuleList,
  } = useQuery(GET_MODULE_LIST);

  // Use selected framework from context instead of hardcoded value
  const {
    data: frameWorkData,
    loading: loadingFrameworkDetails,
    error,
    refetch: refetchFrameworkDetails,
  } = useQuery(GET_FRAMEWORK_DETAIL_BY_ID, {
    variables: { framework_id: selectedFrameworkId },
    skip: !selectedFrameworkId, // Skip query if no framework is selected
  });
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );
  const { data: aiSummaryData, loading: aiLoading } = useQuery(
    GET_AI_EVIDENCE_SUMMARY,
    {
      variables: { frameworkId: selectedFrameworkId },
      skip: !aiEvidenceEnabled || !selectedFrameworkId,
      fetchPolicy: "network-only",
    },
  );
  const aiSummary = aiSummaryData?.getAIEvidenceSummary;
  const aiSummaryLoaded = Boolean(aiSummary);
  const aiPendingCount = aiSummary?.pendingCount || 0;
  // What this viewer can actually action — the strip's button filters to these,
  // so showing the org-wide total alone lands people on an empty list.
  const aiMyPendingCount = (aiSummary?.byCheck || []).reduce(
    (sum, c) => sum + (c.myPendingCount || 0),
    0,
  );
  const aiChecksCount = aiSummary?.checksCount || 0;
  const aiApprovedCount = aiSummary?.approvedCount || 0;
  const aiApprovedPercent = aiSummary?.approvedPercent || 0;
  const aiTotalCount = aiSummary?.totalCount || 0;
  const aiHasIntegrations = (aiSummary?.connectedIntegrations || 0) > 0;
  const aiStripVisible =
    aiEvidenceEnabled &&
    (aiLoading ||
      (aiSummaryLoaded && !aiHasIntegrations) ||
      aiPendingCount > 0 ||
      (aiTotalCount > 0 && aiApprovedPercent >= 80));

  const [controlSummaryAreaWise, setControlSummaryAreaWise] = useState([]);
  const [controlSummaryCriteriaWise, setControlSummaryCriteriaWise] = useState(
    []
  );
  const isDemoInstance = useIsDemoInstance();
  const [frameworkReadiness, setFrameworkReadiness] = useState(0);
  const [totalUniqueControls, setTotalUniqueControls] = useState(0);

  useEffect(() => {
    if (!data || !data?.getModuleList) return;

    const moduleList = data.getModuleList.map((module) => {
      const uniqueChecks = new Map(); // Unique checks per module

      const mappedControls = module?.controls?.map((control) => {
        const controlChecks = control?.checks || [];

        // Track unique checks at the module level
        controlChecks.forEach((check) => {
          if (check?.activeCheckId && !uniqueChecks.has(check.activeCheckId)) {
            uniqueChecks.set(check.activeCheckId, check);
          }
        });

        const totalChecks = controlChecks.length;
        const passingChecks = controlChecks.filter((check) =>
          [CheckStatus.DISABLED, CheckStatus.PASS].includes(check?.checkStatus)
        ).length;

        const readinessPercentage =
          totalChecks > 0 ? (passingChecks / totalChecks) * 100 : 0;

        return {
          ...control,
          readinessPercentage: parseInt(readinessPercentage),
        };
      });

      const totalModuleChecks = uniqueChecks.size;
      const passingModuleChecks = Array.from(uniqueChecks.values()).filter(
        (check) =>
          [CheckStatus.DISABLED, CheckStatus.PASS].includes(check?.checkStatus)
      ).length;

      const moduleReadinessPercentage =
        totalModuleChecks > 0
          ? (passingModuleChecks / totalModuleChecks) * 100
          : 0;

      return {
        area: module?.moduleName,
        controls: module?.controls?.length,
        mappedControl: mappedControls,
        readinessPercentage: parseInt(moduleReadinessPercentage),
      };
    });

    setControlSummaryAreaWise(moduleList);
  }, [data]);

  useEffect(() => {
    if (!frameWorkData || !frameWorkData?.getFrameworkDetailByFrameworkId)
      return;

    const globalUniqueChecks = new Map(); // Unique checks globally
    const globalUniqueControls = new Map(); // Unique controls globally (deduplicated)

    // First pass: Build global unique controls map (similar to ControlsHome.js)
    frameWorkData?.getFrameworkDetailByFrameworkId?.areaDetails?.forEach(
      (area) => {
        area?.criterias?.forEach((criteria) => {
          criteria?.controls?.forEach((control) => {
            if (control?.checks?.length > 0) {
              const controlId = control?.controlsId;
              if (!controlId) return;

              // Track unique control globally (count each control only once)
              if (!globalUniqueControls.has(controlId)) {
                globalUniqueControls.set(controlId, control);

                // Track unique checks globally (only from first occurrence of control)
                control?.checks?.forEach((check) => {
                  if (!globalUniqueChecks.has(check?.activeCheckId)) {
                    globalUniqueChecks.set(check?.activeCheckId, check);
                  }
                });
              }
            }
          });
        });
      }
    );

    // Second pass: Build area summary with deduplicated controls
    const controlSummary =
      frameWorkData?.getFrameworkDetailByFrameworkId?.areaDetails?.map(
        (area) => {
          const areaUniqueChecks = new Map(); // Unique checks for the current area
          const areaUniqueControls = new Map(); // Unique controls for the current area

          const mappedControls = area?.criterias?.flatMap((criteria) =>
            criteria?.controls
              ?.filter((control) => control?.checks?.length > 0) // Exclude controls with no checks
              ?.flatMap((control) => {
                const controlId = control?.controlsId;
                if (!controlId) return []; // Skip if no ID

                // Skip control if already counted for this area
                if (areaUniqueControls.has(controlId)) return [];

                // Track unique control at area level
                areaUniqueControls.set(controlId, control);

                const controlTotalChecks = control?.checks?.length || 0;
                const controlPassingChecks = control?.checks?.filter((check) =>
                  [CheckStatus.DISABLED, CheckStatus.PASS].includes(
                    check?.checkStatus
                  )
                ).length;

                const controlReadinessPercentage =
                  controlTotalChecks > 0
                    ? (controlPassingChecks / controlTotalChecks) * 100
                    : 0;

                // Track unique checks at area level
                control?.checks?.forEach((check) => {
                  if (!areaUniqueChecks.has(check?.activeCheckId)) {
                    areaUniqueChecks.set(check?.activeCheckId, check);
                  }
                });

                return {
                  ...control,
                  readinessPercentage: parseInt(controlReadinessPercentage),
                };
              })
          );

          const totalChecks = areaUniqueChecks.size;
          const passingChecks = Array.from(areaUniqueChecks.values()).filter(
            (check) =>
              [CheckStatus.DISABLED, CheckStatus.PASS].includes(
                check?.checkStatus
              )
          ).length;

          const readinessPercentage =
            totalChecks > 0 ? (passingChecks / totalChecks) * 100 : 0;

          return {
            area: area?.area_name,
            criterias: area?.criterias?.length,
            controls: areaUniqueControls.size,
            mappedControl: mappedControls,
            readinessPercentage: parseInt(readinessPercentage),
            totalChecks,
            passingChecks,
          };
        }
      );

    setControlSummaryCriteriaWise(controlSummary);

    // Framework-level readiness aggregation using globally unique checks
    const totalFrameworkChecks = globalUniqueChecks.size;
    const totalFrameworkPassingChecks = Array.from(
      globalUniqueChecks.values()
    ).filter((check) =>
      [CheckStatus.DISABLED, CheckStatus.PASS].includes(check?.checkStatus)
    ).length;

    const frameworkReadinessPercentage =
      totalFrameworkChecks > 0
        ? (totalFrameworkPassingChecks / totalFrameworkChecks) * 100
        : 0;

    // Store framework-level readiness percentage and total unique controls in state
    setFrameworkReadiness(parseFloat(frameworkReadinessPercentage.toFixed(2)));
    setTotalUniqueControls(globalUniqueControls.size);
  }, [frameWorkData]);
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {!selectedFrameworkId ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            textAlign: "center",
            py: 8,
            px: 4,
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: `1px solid ${theme.customColors.border.main}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Typography
            sx={{
              mt: 3,
              color: theme.customColors.text.primary,
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            Loading Framework
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: theme.customColors.text.secondary,
              fontSize: "14px",
            }}
          >
            Please wait while we load your compliance data...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%" }}>
          {/* Readiness Overview Card */}
          <Card
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              border: `1px solid ${theme.customColors.border.main}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              mb: 3,
            }}
          >
            <CardContent
              sx={{
                p: { xs: 3, md: 4 },
                "&:last-child": { pb: { xs: 3, md: 4 } },
              }}
            >
              {/* Header with Badge */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 4,
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "20px",
                      color: theme.customColors.text.strong,
                      letterSpacing: "-0.02em",
                      mb: 0.5,
                    }}
                  >
                    {selectedFramework?.framework_name
                      ? `${selectedFramework.framework_name} Readiness Overview`
                      : "Readiness Overview"}
                  </Typography>
                </Box>

                {totalUniqueControls > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 2,
                      py: 1,
                      backgroundColor: "#fef3c7",
                      borderRadius: "8px",
                      border: "1px solid #fde68a",
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: "#f59e0b",
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "15px",
                        color: "#92400e",
                      }}
                    >
                      {totalUniqueControls}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#92400e",
                      }}
                    >
                      controls monitored
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Score and Progress Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "auto 1fr" },
                  gap: { xs: 4, md: 5 },
                  alignItems: "center",
                }}
              >
                {/* Score */}
                <Box>
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "56px", md: "64px" },
                        color: theme.customColors.text.primary,
                        lineHeight: 1,
                      }}
                    >
                      {frameworkReadiness}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "32px", md: "36px" },
                        color: theme.customColors.text.secondary,
                        lineHeight: 1,
                      }}
                    >
                      %
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: theme.customColors.text.secondary,
                      fontSize: "14px",
                      mt: 1,
                      fontWeight: 500,
                    }}
                  >
                    Current Progress
                  </Typography>
                </Box>

                {/* Progress Bar */}
                <Box>
                  <Box
                    sx={{
                      height: 10,
                      borderRadius: "100px",
                      backgroundColor: alpha(
                        theme.customColors.border.subtle,
                        0.3
                      ),
                      overflow: "hidden",
                      mt: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${frameworkReadiness}%`,
                        background:
                          "linear-gradient(90deg, #4a7bff 0%, #7b9dff 100%)",
                        borderRadius: "100px",
                        transition: "width 0.5s ease",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    {/* <Typography
                      sx={{
                        color: theme.customColors.text.secondary,
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      Current Progress
                    </Typography> */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "flex-end",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.customColors.text.primary,
                          fontWeight: 500,
                          fontSize: "13px",
                        }}
                      >
                        Your compliance score out of 100 points
                      </Typography>
                      {/* <Typography
                        sx={{
                          color: theme.customColors.text.disabled,
                          fontSize: "12px",
                        }}
                      >
                        of 100%
                      </Typography> */}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {aiEvidenceEnabled && (aiLoading || aiSummaryLoaded) && (
            <AIEvidenceStrip
              pendingCount={aiPendingCount}
              myPendingCount={aiMyPendingCount}
              checksCount={aiChecksCount}
              approvedCount={aiApprovedCount}
              approvedPercent={aiApprovedPercent}
              totalCount={aiTotalCount}
              loading={aiLoading}
              hasIntegrations={aiHasIntegrations}
            />
          )}

          {/* Control Summary Cards */}
          <Grid
            container
            spacing={3}
            sx={{
              height: aiStripVisible
                ? "calc(100vh - 434px)"
                : "calc(100vh - 350px)",
              maxHeight: aiStripVisible
                ? "calc(100vh - 434px)"
                : "calc(100vh - 350px)",
            }}
          >
            {/* {isDemoInstance && (
              <Grid item xs={12} md={6}>
                <ControlSummary
                  heading={"Control Summary By Domain"}
                  data={controlSummaryAreaWise}
                  loading={loading}
                  refetchData={refetchModuleList}
                />
              </Grid>
            )} */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <ControlSummary
                heading={`Control Summary By ${frameworkTerminology.criteria}`}
                data={controlSummaryCriteriaWise}
                loading={loadingFrameworkDetails}
                refetchData={refetchFrameworkDetails}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <OpenTasksCard />
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
